import React from 'react'
import styled from 'styled-components'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Interface
import { TextProps } from 'components/flex/Text/TextShell'

const PrimaryPanel = styled.div`
  background-color: ${({ theme }) => theme.color.primary};
  padding: 1.5rem;

  @media (min-width: 1199px) {
    padding: 50px 107px;
  }

  @media (max-width: 1199px) {
    padding: 40px 15px;
  }
`

const MainTitle = styled.h2`
  color: ${({ theme }) => theme.color.light};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  margin-bottom: 35px;
  text-align: center;

  @media (min-width: 992px) {
    font-size: 36px;
    line-height: 1.25;
  }

  @media (max-width: 991.98px) {
    font-size: 26px;
    line-height: 1.3;
  }
`

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 992px) {
    gap: 1.25rem;
  }
`

const Card = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: rgba(255, 255, 255, 0.2);

  padding: 1rem 1.25rem;

  @media (max-width: 575.98px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  @media (min-width: 992px) {
    padding: 1.25rem 1.5rem;
    gap: 1.25rem;
  }
`

const TimeBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4.25rem;
  padding: 0.5rem 0.65rem;
  background-color: ${({ theme }) => theme.color.secondary};
  border-radius: 4px;
  color: ${({ theme }) => theme.color.primary};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-size: ${({ theme }) => theme.font.size.big};
  font-weight: 400
  line-height: 1.2;
  text-align: center;

  @media (min-width: 992px) {
    font-size: ${({ theme }) => theme.font.size.medium};
    min-width: 4.75rem;
    padding: 0.6rem 0.75rem;
  }
`

const CardBody = styled(ParseContent)`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.color.light};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: ${({ theme }) => theme.font.weight.regular};
  line-height: 1.5;

  & h3,
  & h4 {
    color: ${({ theme }) => theme.color.light};
    font-weight: ${({ theme }) => theme.font.weight.extraBold};
    margin: 0 0 0.5rem 0;
    font-size: ${({ theme }) => theme.font.size.big};

    @media (max-width: 991.98px) {
      font-size: ${({ theme }) => theme.font.size.medium};
    }
  }

  & p {
    color: ${({ theme }) => theme.color.light};
    margin: 0;
    font-size: ${({ theme }) => theme.font.size.medium};
    font-weight: ${({ theme }) => theme.font.weight.regular};

    & strong {
      font-size: 20px;
      font-weight: 700;
      line-height: normal;
    }

    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }
  }

  & a {
    color: ${({ theme }) => theme.color.secondary};
    font-weight: ${({ theme }) => theme.font.weight.bold};

    &:hover {
      color: ${({ theme }) => theme.color.light};
    }
  }
`

function toHtmlFragment(raw: string): string {
  const d = raw.trim()
  if (!d) return ''
  if (d.startsWith('<')) return d
  return `<p>${d}</p>`
}

const TextWorkday: React.FC<TextProps> = ({ fields }) => {
  const items = (fields.workday ?? []).filter((row) => {
    const t = row?.time && String(row.time).trim()
    const d = row?.description && String(row.description).trim()
    return Boolean(t || d)
  })

  return (
    <section>
      <div className="container py-3 py-lg-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <PrimaryPanel>
              {fields?.title && <MainTitle>{fields.title}</MainTitle>}

              {items.length > 0 ? (
                <List>
                  {items.map((row, index) => {
                    const time = row?.time ? String(row.time).trim() : ''
                    const html = toHtmlFragment(row?.description || '')
                    return (
                      <Card key={[time, index].join('-')}>
                        {time ? <TimeBadge>{time}</TimeBadge> : null}
                        {html ? <CardBody content={html} /> : null}
                      </Card>
                    )
                  })}
                </List>
              ) : null}
            </PrimaryPanel>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TextWorkday
